import React, { useState } from 'react';
import './JobReview.css';

const JobReview = ({ jobData, onEdit, onSubmit, onBack, responsibilities, who_you_are, niceToHave, imageUrls }) => {
  // Helper function to format currency
    const formatCurrency = (amount, currencyCode) => {
      if (!amount) return 'Not specified';
      const currencySymbols = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥',
        'KRW': '₩', 'INR': '₹', 'CAD': 'C$', 'AUD': 'A$'
      };
      const symbol = currencySymbols[currencyCode] || currencyCode || '$';
      return `${symbol}${Number(amount).toLocaleString()}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Not specified';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return dateString;
      }
    };
    const [errors, setErrors] = useState([]);

    const checkValue = () => {
      setErrors([]);
      let errorsTem = [];
      if (checkBasicInfo()) {
        errorsTem.push("Missing fields in basic information");
      }
      if (checkDescription()) {
        errorsTem.push("Missing job description");
      }
      if (checkSalary()) {
        errorsTem.push("Missing fields in salary");
      }
      if (checkLocation()) {
        errorsTem.push("Missing fields in location");
      }
      if (checkSkillAndResponibilies()) {
        errorsTem.push("Provide at least 1 skill, responsiblities");
      }
      if (checkBenefits()) {
        errorsTem.push("Provide at least 1 benefit");
      }
      if (checkImages()) {
        errorsTem.push("Provide at least 1 benefit");
      }
      if (errorsTem.length != 0) {
        setErrors(errorsTem);
        return
      }
      onSubmit()
    }

    const checkBasicInfo = () => {
      return !jobData.title || !jobData.job_type || !jobData.experience_level || !jobData.due_date
    }

    const checkDescription = () => {
      return !jobData.description
    }

    const checkSalary = () => {
      return !jobData.salary_lower_rate || !jobData.salary_higher_rate || !jobData.currency_code
    }

    const checkLocation = () => {
      return !jobData.street_address || !jobData.city_name || !jobData.country_name || !jobData.postal_code
    }

    const checkSkillAndResponibilies = () => {
      return jobData.skills.length == 0 || responsibilities.length == 0 || who_you_are.length == 0 || niceToHave.length == 0
    }

    const checkBenefits = () => {
      return jobData.benefits.length == 0
    }

    const checkImages = () => {
      return imageUrls.length == 0
    }
  
    // Helper function to format skills array
    const formatSkills = (skills) => {
      if (!skills || skills.length === 0) return 'None specified';
      return skills.map(skill => skill.name).join(', ');
    };

    const currencySymbols = {
      'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥',
      'KRW': '₩', 'INR': '₹'};

    // Helper function to capitalize text
    const capitalize = (str) => {
      if (!str) return '';
      return str.replace(/\b\w/g, char => char.toUpperCase());
    };

  const ReviewSection = ({ title, children, editSection, error }) => (
    <div className="review-section">
      <div className="review-section-header">
        <h3 className="review-section-title">{title} 
          {error ? <span className='error-label-modern'>fields required</span>: <></>}
        </h3>
        {onEdit && editSection && (
          <button
            onClick={() => onEdit(editSection)}
            className="edit-button"
          >
            Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );

  const ReviewField = ({ label, value, fullWidth = false }) => (
    <div className={`review-field ${fullWidth ? 'full-width' : ''}`}>
      <dt className="review-field-label">{label}</dt>
      <dd className="review-field-value">{value || 'Not specified'}</dd>
    </div>
  );

  return (
    <div className="job-review-container">
      <div className="job-review-header">
        <h1 className="job-review-title">Review Job Posting</h1>
        <p className="job-review-subtitle">Please review all information before submitting your job posting.</p>
        {errors.map((item, err) => (
          <p className="error-label-bordered">{item}</p>
        ))}
      </div>

      {/* Basic Information */}
      <ReviewSection title="Basic Information" 
      error={checkBasicInfo()}
      editSection={1}>
        <dl className="review-fields-grid">
          <ReviewField label="Job Title" value={jobData.title} fullWidth />
          <ReviewField label="Job Type" value={capitalize(jobData.job_type)} />
          <ReviewField label="Experience Level" value={capitalize(jobData.experience_level?.replace(/-/g, ' '))} />
          <ReviewField label="Location Type" value={capitalize(jobData.location_type?.replace(/-/g, ' '))} />
          <ReviewField label="Application Deadline" value={formatDate(jobData.due_date)} />
        </dl>
      </ReviewSection>

      {/* Job Description */}
      <ReviewSection title="Job Description"
      error={ checkDescription()}
      editSection={2}>
        <div className="description-content">
          <p className="description-text">
            {jobData.description || 'No description provided'}
          </p>
        </div>
      </ReviewSection>

      {/* Salary Information */}
      <ReviewSection title="Compensation" 
      error={ checkSalary() }
      editSection={10}>
        <dl className="review-fields-grid">
          <ReviewField 
            label="Salary Range" 
            value={
              jobData.salary_lower_rate || jobData.salary_higher_rate
                ? `${formatCurrency(jobData.salary_lower_rate, jobData.currency_code)} - ${formatCurrency(jobData.salary_higher_rate, jobData.currency_code)}`
                : 'Not specified'
            }
            fullWidth
          />
          <ReviewField label="Currency" value={jobData.currency_code} />
        </dl>
      </ReviewSection>

      {/* Location */}
      <ReviewSection title="Location" 
      error ={ checkLocation() }
      editSection={11}>
        <dl className="review-fields-grid">
          <ReviewField label="Street Address" value={jobData.street_address} fullWidth />
          <ReviewField label="City" value={jobData.city_name} />
          <ReviewField label="State/Province" value={jobData.state || ''} />
          <ReviewField label="Country" value={jobData.country_name} />
          <ReviewField label="Postal Code" value={jobData.postal_code} />
        </dl>
      </ReviewSection>

      {/* Skills & Requirements */}
      <ReviewSection title="Skills & Requirements" 
        error ={ checkSkillAndResponibilies()}
        editSection={5}>
        <dl className="requirements-list">
          <ReviewField label="Required Skills" value={formatSkills(jobData.skills)} fullWidth />
          
          {responsibilities && responsibilities.length > 0 && (
            <div className="review-field full-width">
              <dt className="review-field-label">Responsibilities</dt>
              <dd className="review-field-value">
                <ul className="requirement-list">
                  {responsibilities.map((req, index) => (
                    <li key={index} className="requirement-item">
                      {req}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}

          {who_you_are && who_you_are.length > 0 && (
            <div className="review-field full-width">
              <dt className="review-field-label">Essential Requirements</dt>
              <dd className="review-field-value">
                <ul className="requirement-list">
                  {who_you_are.map((req, index) => (
                    <li key={index} className="requirement-item">
                      {req}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}

        {niceToHave && niceToHave.length > 0 && (
            <div className="review-field full-width">
              <dt className="review-field-label">Preferred Qualifications</dt>
              <dd className="review-field-value">
                <ul className="requirement-list">
                  {niceToHave.map((req, index) => (
                    <li key={index} className="requirement-item">
                      {req}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
          
          
        </dl>
      </ReviewSection>

      {/* Benefits */}
        <ReviewSection title="Benefits" 
        error ={ checkBenefits() }
        editSection={12}>
          <div className="benefits-list">
            {jobData.benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <h4 className="benefit-name">{benefit.benefit_name}</h4>
                {benefit.benefit_description && (
                  <p className="benefit-description">{benefit.benefit_description}</p>
                )}
              </div>
            ))}
          </div>
        </ReviewSection>

      {/* Image Gallery */}
        <ReviewSection title="Image Gallery" 
        error ={ checkImages() }
        editSection={13}>
          <div className="image-gallery">
            {imageUrls.map((url, index) => (
              <div key={index} className="gallery-image-container">
                <img
                  src={url}
                  alt={`Job posting image ${index + 1}`}
                  className="gallery-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-error" style={{ display: 'none' }}>
                  <div className="error-content">
                    <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="error-text">Failed to load image</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ReviewSection>


      {/* Action Buttons */}
      <div className="action-buttons-container">
        <div className="action-buttons-wrapper">
          <button onClick={onBack} className="back-button">
            ← Back to Edit
          </button>
          
          <div className="action-buttons-right">
            <button onClick={() => onEdit(1)} className="edit-all-button">
              Edit All
            </button>
            <button onClick={() => checkValue()} className="submit-button">
              Submit Job Posting
            </button>
          </div>
        </div>
      </div>

      {/* Data Summary */}
      {/* <div className="debug-section">
        <details>
          <summary className="debug-summary">View Raw Data (for debugging)</summary>
          <pre className="debug-content">
            {JSON.stringify(jobData, null, 2)}
          </pre>
        </details>
      </div> */}

    </div>
  );
};

export default JobReview;